<template>
    <PrimeAvatar size="md" :class="{ 'person-avatar--waiting': waiting }">
        <img
            v-if="avatarUrl && !failed"
            :src="avatarUrl"
            alt=""
            draggable="false"
            class="person-avatar__image"
            @error="failed = true"
        >
        <template v-else>{{ initials }}</template>
    </PrimeAvatar>
</template>

<script setup lang="ts">
/**
 * One person's avatar. `avatarUrl` is the server-derived SparkVault Identity
 * address (Person::$avatar_url); that host answers 404 for anyone without a
 * public avatar — a normal answer, not an error — so a failed load falls back
 * to the monogram, same as an absent URL. Someone with no name, blank or
 * absent, is drawn with the monogram's placeholder, never an initial made up
 * for them.
 *
 * `waiting` draws the place a person goes before they are one of the reader's
 * people, such as someone whose invitation is not accepted yet: dashed and
 * unfilled (DESIGN.md, "Avatars, chips and counts"), so a list of people who
 * are waiting reads apart from a list of people who are there by shape as well
 * as by its words.
 */
import { computed, ref, toRef, watch } from 'vue';
import { initials as nameInitials } from '@privacy-bee/format/text';

const props = defineProps({
    name: {
        type: String as () => string | null,
        required: false,
        default: null,
    },
    avatarUrl: {
        type: String as () => string | null,
        required: false,
        default: null,
    },
    waiting: {
        type: Boolean,
        required: false,
        default: false,
    },
});

const failed = ref(false);
// A row is recycled across people as a list re-renders, so the error flag has
// to follow the URL rather than the component instance.
watch(toRef(props, 'avatarUrl'), () => {
    failed.value = false;
});

const initials = computed(() => nameInitials(props.name));
</script>

<style lang="scss" scoped>
.person-avatar__image {
    @apply w-full h-full rounded-full object-cover select-none;
}

// One edge, the dashed one, and no fill: nothing about the person is there yet.
.person-avatar--waiting {
    border: 1px dashed var(--hairline-strong);
    background: none;
}
</style>
